import React, { ReactNode, useContext, useEffect, useState } from 'react';

import EditModal from '@totem/components/endoflife/EditModal';
import EndOfLifeContext from '@totem/components/endoflife/EndOfLifeContext';
import { Product, ProductReport } from '@totem/components/endoflife/types';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { FilterField } from '@totem/components/ui_components/reportSettings/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Params } from '@totem/types/common';
import { isNotNull } from '@totem/utilities/common';
import { END_OF_LIFE_ENDPOINT } from '@totem/utilities/endpoints';

export const containerConfiguration: FilterField[] = [
  {
    key: 'productType',
    label: 'Product Type',
    sortField: 'productType',
    filterField: 'productType',
    multiple: true,
    values: [],
  },
  {
    key: 'product',
    label: 'Product',
    sortField: 'product',
    filterField: 'product',
    multiple: true,
    values: [],
  },
  {
    key: 'name',
    label: 'Name',
    sortField: 'name',
    filterField: 'name',
    multiple: true,
    values: [],
  },
  {
    key: 'cycle',
    label: 'Cycle',
    sortField: 'cycle',
    filterField: 'cycle',
    multiple: true,
    values: [],
  },
  {
    key: 'latestVersion',
    label: 'Latest Version',
    sortField: 'latestVersion',
    filterField: 'latestVersion',
    multiple: true,
    values: [],
  },
  {
    key: 'releaseDate',
    label: 'Release Date',
    sortField: 'releaseDate',
    filterField: 'releaseDate',
    multiple: true,
    values: [],
  },
  {
    key: 'endOfSupportDate',
    label: 'End of Support',
    sortField: 'endOfSupportDate',
    filterField: 'endOfSupportDate',
    multiple: true,
    values: [],
  },
  {
    key: 'endOfLifeDate',
    label: 'End of Life',
    sortField: 'endOfLifeDate',
    filterField: 'endOfLifeDate',
    multiple: true,
    values: [],
  },
  {
    key: 'discontinuedDate',
    label: 'Discontinued',
    sortField: 'discontinuedDate',
    filterField: 'discontinuedDate',
    multiple: true,
    values: [],
  },
];

type Props = {
  children?: ReactNode;
  onRecordTotalChanged?: (total: number) => void;
};

const EndOfLifeContainer = ({ children, onRecordTotalChanged }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const {
    buildParameters,
    filterUpdateCount,
    sortingUpdateCount,
    pagingUpdateCount,
  } = useContext(InputContext);

  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [reportData, setReportData] = useState<ProductReport>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product>(null);
  const [selectedAction, setSelectedAction] = useState<string>('');

  useEffect(() => {
    if (isNotNull(onRecordTotalChanged)) {
      onRecordTotalChanged(totalRecords);
    }
  }, [onRecordTotalChanged, totalRecords]);

  useEffect(() => {
    setRefreshData(true);
  }, [filterUpdateCount, pagingUpdateCount, sortingUpdateCount]);

  useEffect(() => {
    if (refreshData) {
      setRefreshData(false);

      setIsLoading(true);
      const params: Params = buildParameters();
      fetch(`${END_OF_LIFE_ENDPOINT}`, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(params),
      })
        .then((res) => res.json())
        .then((result: ProductReport) => {
          setReportData(result);

          setTotalRecords(result.paging.totalRecords);
        })
        .then(() => {
          setIsLoading(false);
        });
    }
  }, [refreshData, getApiToken]);

  const handleAction = (action: string, product: Product) => {
    switch (action) {
      case 'closeEdit_NoRefresh':
        setSelectedAction('');

        setSelectedProduct(null);
        break;
      case 'closeEdit_Refresh':
        setSelectedAction('');

        setSelectedProduct(null);

        setRefreshData(true);
        break;
      default:
        setSelectedAction(action);

        setSelectedProduct(product);
    }
  };

  return (
    <EndOfLifeContext.Provider
      value={{
        loading: isLoading,
        reportData,
        totalRecords,
        onAction: handleAction,
      }}
    >
      <div>{children}</div>
      {selectedAction === 'edit' && (
        <EditModal
          product={selectedProduct}
          visible={selectedAction === 'edit'}
        />
      )}
    </EndOfLifeContext.Provider>
  );
};
export default EndOfLifeContainer;
